'use client'

import Image from 'next/image'
import { cn } from '@/lib/utils'
import { Clock, Calendar } from 'lucide-react'

interface TimelineEvent {
  id: string
  date: string
  title: string
  description?: string
  imageUrl?: string
}

interface TimelineBlockContent {
  title?: string
  events: TimelineEvent[]
}

interface TimelineBlockProps {
  content: TimelineBlockContent
  className?: string
}

export function TimelineBlock({ content, className }: TimelineBlockProps) {
  const events = content.events || []

  if (!events.length) return null

  // Sort events chronologically. Dates can be freeform strings like
  // "2019", "Nov 2025", "Jan 2026", "March 15, 2024", etc.
  // Try Date.parse first (handles most formats); fall back to string compare.
  const sortedEvents = [...events].sort((a, b) => {
    const aTime = Date.parse(a.date)
    const bTime = Date.parse(b.date)
    if (!isNaN(aTime) && !isNaN(bTime)) return aTime - bTime
    if (!isNaN(aTime)) return -1
    if (!isNaN(bTime)) return 1
    return a.date.localeCompare(b.date)
  })

  return (
    <div className={cn('space-y-4', className)}>
      {content.title && (
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Clock className="h-5 w-5 text-violet-400" />
          {content.title}
        </h3>
      )}
      
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-[23px] top-2 bottom-2 w-0.5 bg-gradient-to-b from-violet-500 via-fuchsia-500 to-violet-500/30" />
        
        {/* Events */}
        <div className="space-y-6">
          {sortedEvents.map((event, idx) => (
            <div key={event.id} className="relative flex gap-4">
              {/* Dot - solid background to obscure the line behind */}
              <div className={cn(
                "w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 z-10",
                "bg-zinc-900 border-2",
                idx === 0 ? "border-violet-500" : "border-violet-500/50"
              )}>
                <Calendar className={cn(
                  "h-5 w-5",
                  idx === 0 ? "text-violet-400" : "text-violet-500/70"
                )} />
              </div>
              
              {/* Content */}
              <div className={cn(
                "flex-1 pb-2",
                idx === sortedEvents.length - 1 && "pb-0"
              )}>
                <div className="flex flex-col sm:flex-row sm:items-start gap-3">
                  {/* Event info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-violet-400 font-semibold text-sm">
                        {event.date}
                      </span>
                    </div>
                    <h4 className="text-white font-medium text-base mb-1">
                      {event.title}
                    </h4>
                    {event.description && (
                      <p className="text-zinc-400 text-sm leading-relaxed">
                        {event.description}
                      </p>
                    )}
                  </div>
                  
                  {/* Event image */}
                  {event.imageUrl && (
                    <div className="w-full sm:w-32 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-zinc-800 relative">
                      <Image
                        src={event.imageUrl}
                        alt={event.title}
                        fill
                        sizes="(max-width: 640px) 100vw, 128px"
                        className="object-cover"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

